import type { CustomerWithCrop } from '../../services/cropStatus.service';
import type { SeasonTime } from '../../services/seasonTime.service';

// ─── Calendar math helpers ────────────────────────────────────────────────────

const CUMUL_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const YEAR_DAYS = 365;

/** 0-indexed day of year (0 = Jan 1, 364 = Dec 31, non-leap year). */
export function dayOfYear(month: number, day: number): number {
  return CUMUL_DAYS[month - 1] + day - 1;
}

/**
 * Returns bar segments as { left, width } percentage values (0–100) for a
 * season. Non-wrapping seasons produce one segment; year-spanning seasons
 * produce two segments (one from start→Dec 31, one from Jan 1→end).
 */
export function seasonBarSegments(
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number,
): Array<{ left: number; width: number }> {
  const startDoy = dayOfYear(startMonth, startDay);
  const endDoy   = dayOfYear(endMonth,   endDay);

  if (endDoy >= startDoy) {
    return [{
      left:  (startDoy / YEAR_DAYS) * 100,
      width: ((endDoy - startDoy + 1) / YEAR_DAYS) * 100,
    }];
  }

  // Wrapping season: two segments
  return [
    { left: (startDoy / YEAR_DAYS) * 100, width: ((YEAR_DAYS - startDoy) / YEAR_DAYS) * 100 },
    { left: 0, width: ((endDoy + 1) / YEAR_DAYS) * 100 },
  ];
}

/**
 * Filters crop documents against a set of valid (cropTypeId, regionId) pairs
 * derived from the matching seasons list, then deduplicates by
 * (customerId, cropTypeId, regionId).
 *
 * This is the critical guard that prevents crops whose pair has no season
 * definition from appearing in results — particularly important for the
 * fast-path query that returns all crops unfiltered.
 */
export function filterAndDeduplicateCrops(
  allCropResults: CustomerWithCrop[][],
  matchingSeasons: Pick<SeasonTime, 'cropTypeId' | 'regionId'>[],
): CustomerWithCrop[] {
  const validPairs = new Set(matchingSeasons.map(s => `${s.cropTypeId}|${s.regionId}`));

  const seen = new Set<string>();
  const unique: CustomerWithCrop[] = [];

  for (const batch of allCropResults) {
    for (const item of batch) {
      if (!validPairs.has(`${item.cropTypeId}|${item.regionId}`)) continue;
      const key = `${item.customerId}|${item.cropTypeId}|${item.regionId}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    }
  }

  return unique;
}
